import React from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Form,
  Dropdown,
  Button,
  Segment,
  Header,
  Message,
} from "semantic-ui-react";

const colorOptions = [
  { key: "purple", text: "Purple", value: "#8b5cf6" },
  { key: "teal", text: "Teal", value: "#06b6d4" },
  { key: "orange", text: "Orange", value: "#f97316" },
  { key: "blue", text: "Blue", value: "#3b82f6" },
  { key: "green", text: "Green", value: "#22c55e" },
  { key: "red", text: "Red", value: "#ef4444" },
];

export const InfoEventModal = ({
  open,
  onClose,
  event,
  canEdit,
  onEdit,
  onDelete,
}) => (
  <Modal open={open} onClose={onClose} size="small">
    <Modal.Header>Event Information</Modal.Header>
    <Modal.Content>
      {event && (
        <Segment>
          <Header as="h3" style={{ color: event.color || "#8b5cf6" }}>
            {event.title}
          </Header>
          <p>
            <strong>Start Date:</strong>{" "}
            {new Date(event.start).toLocaleDateString()}
          </p>
          <p>
            <strong>End Date:</strong>{" "}
            {new Date(event.end).toLocaleDateString()}
          </p>
          <p>
            <strong>Description:</strong>{" "}
            {event.description || "No description available"}
          </p>
        </Segment>
      )}
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={onClose}>Close</Button>
      {canEdit && event && (
        <>
          <Button
            color="orange"
            onClick={() => {
              onClose();
              onEdit(event);
            }}
          >
            Edit Event
          </Button>
          <Button
            color="red"
            onClick={() => {
              onClose();
              onDelete(event);
            }}
          >
            Delete Event
          </Button>
        </>
      )}
    </Modal.Actions>
  </Modal>
);

InfoEventModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.shape({
    color: PropTypes.string,
    title: PropTypes.string,
    start: PropTypes.string,
    end: PropTypes.string,
    description: PropTypes.string,
  }),
  canEdit: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export const AddEventModal = ({
  open,
  onClose,
  formData,
  onChange,
  onSubmit,
  disableSubmit,
}) => (
  <Modal open={open} onClose={onClose} size="small">
    <Modal.Header>Add New Event</Modal.Header>
    <Modal.Content>
      <Form>
        <Form.Input
          label="Title"
          name="title"
          value={formData.title}
          onChange={onChange}
          required
        />
        <Form.Input
          label="Start Date"
          name="start"
          type="date"
          value={formData.start}
          onChange={onChange}
          required
        />
        <Form.Input
          label="End Date"
          name="end"
          type="date"
          value={formData.end}
          onChange={onChange}
          required
        />
        <Form.TextArea
          label="Description"
          name="description"
          value={formData.description}
          onChange={onChange}
        />
        <Form.Field required>
          <label>Event Color</label>
          <Dropdown
            fluid
            selection
            options={colorOptions}
            name="color"
            value={formData.color}
            onChange={onChange}
          />
        </Form.Field>
      </Form>
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={onClose}>Cancel</Button>
      <Button color="green" onClick={onSubmit} disabled={disableSubmit}>
        Add Event
      </Button>
    </Modal.Actions>
  </Modal>
);

AddEventModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    start: PropTypes.string.isRequired,
    end: PropTypes.string.isRequired,
    description: PropTypes.string,
    color: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  disableSubmit: PropTypes.bool.isRequired,
};

export const EditEventModal = ({
  open,
  onClose,
  formData,
  onChange,
  onSubmit,
  disableSubmit,
}) => (
  <Modal open={open} onClose={onClose} size="small">
    <Modal.Header>Edit Event</Modal.Header>
    <Modal.Content>
      <Form>
        <Form.Input
          label="Title"
          name="title"
          value={formData.title}
          onChange={onChange}
          required
        />
        <Form.Input
          label="Start Date"
          name="start"
          type="date"
          value={formData.start}
          onChange={onChange}
          required
        />
        <Form.Input
          label="End Date"
          name="end"
          type="date"
          value={formData.end}
          onChange={onChange}
          required
        />
        <Form.TextArea
          label="Description"
          name="description"
          value={formData.description}
          onChange={onChange}
        />
        <Form.Field required>
          <label>Event Color</label>
          <Dropdown
            fluid
            selection
            options={colorOptions}
            name="color"
            value={formData.color}
            onChange={onChange}
          />
        </Form.Field>
      </Form>
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={onClose}>Cancel</Button>
      <Button color="green" onClick={onSubmit} disabled={disableSubmit}>
        Save Changes
      </Button>
    </Modal.Actions>
  </Modal>
);

EditEventModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    start: PropTypes.string.isRequired,
    end: PropTypes.string.isRequired,
    description: PropTypes.string,
    color: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  disableSubmit: PropTypes.bool.isRequired,
};

export const DeleteEventModal = ({ open, onClose, event, onDelete }) => (
  <Modal open={open} onClose={onClose} size="small">
    <Modal.Header>Delete Event</Modal.Header>
    <Modal.Content>
      {event && (
        <>
          <p>Are you sure you want to delete this event?</p>
          <Message warning>
            <Message.Header>{event.title}</Message.Header>
            <p>
              <strong>Start:</strong>{" "}
              {new Date(event.start).toLocaleDateString()}
              <br />
              <strong>End:</strong> {new Date(event.end).toLocaleDateString()}
            </p>
          </Message>
          <p>
            <strong>This action cannot be undone.</strong>
          </p>
        </>
      )}
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={onClose}>Cancel</Button>
      <Button color="red" onClick={onDelete}>
        Delete Event
      </Button>
    </Modal.Actions>
  </Modal>
);

DeleteEventModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.shape({
    title: PropTypes.string,
    start: PropTypes.string,
    end: PropTypes.string,
  }),
  onDelete: PropTypes.func.isRequired,
};
